if (!customElements.get('ll-country-modal')) {
  customElements.define(
    'll-country-modal',
    class LLCountryModal extends HTMLElement {
      constructor() {
        super();
        this.selectedValue = null;
        this.selectedCurrency = null;
        this.selectedSymbol = null;
        this.belgiumLang = null;
      }

      connectedCallback() {
        this.openBtn = this.querySelector('[data-ll-country-open]');
        this.panel = this.querySelector('[data-ll-country-panel]');
        this.dialog = this.querySelector('[data-ll-country-dialog]');
        this.searchInput = this.querySelector('[data-ll-country-search]');
        this.list = this.querySelector('[data-ll-country-list]');
        this.empty = this.querySelector('[data-ll-country-empty]');
        this.confirmBtn = this.querySelector('[data-ll-country-confirm]');
        this.input = this.querySelector('[data-ll-country-input]');
        this.languageInput = this.querySelector('[data-ll-language-input]');
        this.form = this.closest('form') || this.querySelector('form');
        this.items = Array.from(this.querySelectorAll('.ll-country-modal__item'));
        this.options = Array.from(this.querySelectorAll('.ll-country-modal__option'));
        this.availableLanguages = this.readAvailableLanguages();

        this.onOpen = this.onOpen.bind(this);
        this.onClose = this.onClose.bind(this);
        this.onSearch = this.onSearch.bind(this);
        this.onKeyUp = this.onKeyUp.bind(this);
        this.onConfirm = this.onConfirm.bind(this);

        this.openBtn?.addEventListener('click', this.onOpen);
        this.searchInput?.addEventListener('input', this.onSearch);
        this.confirmBtn?.addEventListener('click', this.onConfirm);
        this.querySelectorAll('[data-ll-country-close]').forEach((el) => {
          el.addEventListener('click', this.onClose);
        });
        this.options.forEach((btn) => {
          btn.addEventListener('click', () => this.selectOption(btn));
        });
        this.querySelectorAll('[data-ll-belgium-lang]').forEach((btn) => {
          btn.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.setBelgiumLang(btn.getAttribute('data-ll-belgium-lang'));
          });
        });

        const current = this.options.find((btn) => btn.classList.contains('is-selected'));
        if (current) this.selectOption(current, true);
      }

      readAvailableLanguages() {
        const el = this.querySelector('[data-ll-available-languages]');
        if (!el) return [];
        try {
          const parsed = JSON.parse(el.textContent || '[]');
          return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
          return [];
        }
      }

      /**
       * Same map as old React i18n/index.ts (country selection sets language).
       */
      languageForCountry(countryCode, belgiumLang) {
        const code = (countryCode || '').toUpperCase();
        if (code === 'BE') return belgiumLang === 'fr' ? 'fr' : belgiumLang === 'nl' ? 'nl' : null;
        if (code === 'CH') return ['de', 'fr', 'it'].includes(belgiumLang) ? belgiumLang : null;
        if (code === 'GB') return 'en-GB';
        if (['DE', 'AT', 'LI'].includes(code)) return 'de';
        if (['FR', 'LU', 'MC'].includes(code)) return 'fr';
        if (['IT', 'SM', 'VA'].includes(code)) return 'it';
        if (
          [
            'ES',
            'MX',
            'AR',
            'CO',
            'CL',
            'PE',
            'VE',
            'EC',
            'GT',
            'CU',
            'BO',
            'DO',
            'HN',
            'PY',
            'SV',
            'NI',
            'CR',
            'PA',
            'UY',
            'PR',
          ].includes(code)
        ) {
          return 'es';
        }
        if (code === 'NL') return 'nl';
        if (code === 'PL') return 'pl';
        return 'en';
      }

      resolveShopifyLocale(wanted) {
        if (!wanted) return this.languageInput?.value || 'en';
        const available = this.availableLanguages.map((code) => String(code));
        if (!available.length) return wanted;
        const exact = available.find((code) => code.toLowerCase() === wanted.toLowerCase());
        if (exact) return exact;
        const root = wanted.split('-')[0].toLowerCase();
        const byRoot = available.find((code) => {
          const lower = code.toLowerCase();
          return lower === root || lower.startsWith(`${root}-`);
        });
        return byRoot || available.find((code) => code.toLowerCase() === 'en') || wanted;
      }

      onOpen(event) {
        event.preventDefault();
        if (this.searchInput) {
          this.searchInput.value = '';
          this.onSearch();
        }
        this.panel?.removeAttribute('hidden');
        this.openBtn?.setAttribute('aria-expanded', 'true');
        document.body.classList.add('ll-country-modal-open');
        document.addEventListener('keyup', this.onKeyUp);
        requestAnimationFrame(() => {
          this.searchInput?.focus();
        });
      }

      onClose(event) {
        if (event) event.preventDefault();
        this.panel?.setAttribute('hidden', '');
        this.openBtn?.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('ll-country-modal-open');
        document.removeEventListener('keyup', this.onKeyUp);
        this.openBtn?.focus();
      }

      onKeyUp(event) {
        if (event.code === 'Escape') this.onClose();
      }

      onSearch() {
        const query = (this.searchInput?.value || '').trim().toLowerCase();
        let visible = 0;
        this.items.forEach((item) => {
          const name = (item.dataset.countryName || '').toLowerCase();
          const code = (item.dataset.countryCode || '').toLowerCase();
          const match = !query || name.includes(query) || code.includes(query);
          item.hidden = !match;
          if (match) visible += 1;
        });
        if (this.empty) this.empty.hidden = visible > 0;
      }

      isMultiLangCountry(code) {
        return code === 'BE' || code === 'CH';
      }

      hideBelgiumPickers() {
        this.querySelectorAll('[data-ll-belgium-picker]').forEach((el) => {
          el.setAttribute('hidden', '');
        });
      }

      /* Language picker for multilingual countries (Belgium: nl/fr, Switzerland: de/fr/it). */
      setBelgiumLang(lang) {
        this.belgiumLang = lang || null;
        this.querySelectorAll('[data-ll-belgium-lang]').forEach((btn) => {
          const active = btn.getAttribute('data-ll-belgium-lang') === this.belgiumLang;
          btn.classList.toggle('is-selected', active);
          btn.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        this.updateConfirmState();
      }

      selectOption(btn, updateConfirm = true) {
        this.options.forEach((option) => {
          const selected = option === btn;
          option.classList.toggle('is-selected', selected);
          option.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });
        this.selectedValue = btn.dataset.value;
        this.selectedCurrency = btn.dataset.currency;
        this.selectedSymbol = btn.dataset.symbol;

        this.hideBelgiumPickers();
        const item = btn.closest('.ll-country-modal__item');
        const picker = item?.querySelector('[data-ll-belgium-picker]');
        if (this.isMultiLangCountry(this.selectedValue) && picker) {
          picker.removeAttribute('hidden');
          const current = ((window.Shopify && Shopify.locale) || 'en').toLowerCase().slice(0, 2);
          const offered = Array.from(picker.querySelectorAll('[data-ll-belgium-lang]')).map((b) => b.getAttribute('data-ll-belgium-lang'));
          if (!this.belgiumLang || !offered.includes(this.belgiumLang)) {
            this.belgiumLang = offered.includes(current) ? current : null;
          }
          this.setBelgiumLang(this.belgiumLang);
        } else {
          this.belgiumLang = null;
        }

        if (updateConfirm) this.updateConfirmState();
      }

      updateConfirmState() {
        if (!this.confirmBtn) return;
        const belgiumBlocked = this.isMultiLangCountry(this.selectedValue) && !this.belgiumLang;
        this.confirmBtn.disabled = !this.selectedValue || belgiumBlocked;
        const continueLabel =
          this.confirmBtn.dataset.continueLabel || this.confirmBtn.textContent.split('·')[0].trim();
        this.confirmBtn.dataset.continueLabel = continueLabel;
        if (this.selectedSymbol) {
          this.confirmBtn.textContent = `${continueLabel} · ${this.selectedSymbol}`;
        } else {
          this.confirmBtn.textContent = continueLabel;
        }
      }

      isLocalProxy() {
        const host = window.location.hostname;
        return host === '127.0.0.1' || host === 'localhost';
      }

      /**
       * theme:dev 404s /fr-in/ (and other lang-country Markets folders).
       * Keep language-only path: /fr/products/... — never /fr-in/...
       * en-GB uses /en-gb/ when that locale is published.
       */
      buildLocalCountryUrl(langIso) {
        const iso = langIso || (window.Shopify && Shopify.locale) || 'en';
        const lower = String(iso).toLowerCase();
        let newRoot = '/';
        if (lower === 'en-gb' || lower === 'en_gb') {
          newRoot = '/en-gb/';
        } else {
          const lang = lower.split('-')[0];
          if (lang && lang !== 'en') newRoot = `/${lang}/`;
        }

        let rest = window.location.pathname || '/';
        rest = rest.replace(
          /^\/[a-z]{2}(?:-[a-z]{2})?(?=pages|products|collections|cart|blogs|policies|account|search)/i,
          '/'
        );
        rest = rest.replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/|$)/i, '') || '/';
        if (!rest.startsWith('/')) rest = `/${rest}`;

        const path =
          rest === '/'
            ? newRoot === '/'
              ? '/'
              : newRoot
            : newRoot === '/'
              ? rest
              : `${newRoot.replace(/\/$/, '')}${rest}`;
        return `${path}${window.location.search || ''}${window.location.hash || ''}`;
      }

      onConfirm(event) {
        event.preventDefault();
        if (!this.selectedValue || !this.input) return;
        if (this.isMultiLangCountry(this.selectedValue) && !this.belgiumLang) return;

        const wantedLang = this.languageForCountry(this.selectedValue, this.belgiumLang);
        const locale = this.resolveShopifyLocale(wantedLang || 'en');

        this.input.value = this.selectedValue;
        if (this.languageInput) this.languageInput.value = locale;
        const localeInput = this.querySelector('input[name="locale_code"]');
        if (localeInput) localeInput.value = locale;

        const form = this.form || this.closest('form');

        if (this.isLocalProxy()) {
          try {
            localStorage.setItem('ll-dev-country', this.selectedValue.toUpperCase());
          } catch (e) {}
          window.location.assign(this.buildLocalCountryUrl(locale));
          return;
        }

        if (form) {
          const nextUrl = this.buildLocalCountryUrl(locale);
          let returnTo = form.querySelector('input[name="return_to"]');
          if (!returnTo) {
            returnTo = document.createElement('input');
            returnTo.type = 'hidden';
            returnTo.name = 'return_to';
            form.appendChild(returnTo);
          }
          returnTo.value = nextUrl;
          form.submit();
        }
      }
    }
  );
}
